import { useQuery } from '@tanstack/react-query'
import { tickerOfSymbol } from '@greencandle/api-client'
import { api } from '../../api'
import { Failed, pct } from '../primitives'

/**
 * QNT-1 / SRV-2: the risk reading, auto-fetched.
 *
 * <p>A status, not a proposal - which is why it loads with the tab rather than sitting behind a
 * button the way `Optimiser` and `StressPanel` do.
 */

/**
 * FR-G5, computed by the Python sidecar (ML-1).
 *
 * <p>The first thing on this screen that Java could not reasonably have produced. It is also
 * the first analytics figure the app has shown at all, so it follows FR-G1 literally: the
 * estimation window, the observation count and the confidence level sit next to the numbers,
 * and NFR-L1's "not advice" line is on the panel rather than in a tooltip.
 */
export function RiskPanel({ accountId }: { accountId: string }) {
  const risk = useQuery({
    queryKey: ['risk', accountId],
    queryFn: () => api.risk(accountId),
    placeholderData: (previous) => previous,
  })

  return (
    <section className="panel">
      <div className="panel-header">
        <h3>Portfolio risk (Python engine)</h3>
        <span className="panel-note">
          {risk.data?.available
            ? `${risk.data.observations} observations · ${risk.data.from} — ${risk.data.to}`
            : 'FR-G5 · historical estimate'}
        </span>
      </div>

      {risk.isError ? (
        <Failed error={risk.error} />
      ) : risk.isPending ? (
        <p className="panel-foot">Loading…</p>
      ) : !risk.data.available ? (
        <div className="panel-gap">
          <span className="badge badge-custom">not available</span>
          <p>{risk.data.reason}</p>
        </div>
      ) : (
        <>
          <div className="risk-figures">
            <RiskFigure
              label="Volatility (annual)"
              value={risk.data.portfolio?.annualisedVolatility}
              hint="Standard deviation of log returns, scaled to a year"
            />
            <RiskFigure
              label={`VaR ${pct(risk.data.confidence)}`}
              value={risk.data.portfolio?.valueAtRisk}
              tone="down"
              hint="Historical rather than parametric: a fitted normal understates exactly the days this figure exists for"
            />
            <RiskFigure
              label={`Expected Shortfall ${pct(risk.data.confidence)}`}
              value={risk.data.portfolio?.expectedShortfall}
              tone="down"
              hint="Mean loss on the days that breached VaR"
            />
            <RiskFigure
              label="Max drawdown"
              value={risk.data.portfolio?.maxDrawdown}
              tone="down"
              hint="Worst peak-to-trough fall in the window"
            />
          </div>

          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Asset</th>
                  <th className="right">Share of money</th>
                  <th className="right">Share of risk</th>
                  <th className="right">Volatility</th>
                  <th className="right">Max drawdown</th>
                </tr>
              </thead>
              <tbody>
                {risk.data.assets.map((asset) => {
                  // The comparison the table exists for: a position can be a twentieth of the
                  // money and a third of the risk, and only the pair of columns shows it.
                  // Only meaningful for a long position: a short's weight is negative, and
                  // "more risk than money" does not describe it.
                  const carriesMore =
                    asset.riskContribution != null &&
                    asset.weight != null &&
                    asset.weight > 0 &&
                    asset.riskContribution > asset.weight * 1.25
                  return (
                    <tr key={asset.symbol}>
                      <td>
                        <strong>{tickerOfSymbol(asset.symbol)}</strong>
                      </td>
                      <td className="right num">{pct(asset.weight)}</td>
                      <td className={`right num ${carriesMore ? 'down' : ''}`}>
                        {pct(asset.riskContribution)}
                      </td>
                      <td className="right num">{pct(asset.annualisedVolatility)}</td>
                      <td className="right num">{pct(asset.maxDrawdown)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {risk.data.correlations.length > 0 && (
            <div className="risk-pairs">
              <span className="risk-pairs-label">Strongest correlations</span>
              {risk.data.correlations.slice(0, 5).map((pair) => (
                <span className="risk-pair" key={`${pair.a}-${pair.b}`}>
                  {pair.a} / {pair.b}
                  <strong>{pair.correlation?.toFixed(2)}</strong>
                </span>
              ))}
            </div>
          )}

          {risk.data.skipped.length > 0 && (
            <p className="panel-foot warn">
              Excluded: {risk.data.skipped.join(', ')} — too little price history in the window to
              compare over.
            </p>
          )}
          {risk.data.notes.map((note) => (
            <p className="panel-foot" key={note}>
              {note}
            </p>
          ))}
          <p className="panel-foot">
            Historical estimate over {risk.data.observations} common daily observations from{' '}
            {risk.data.from} to {risk.data.to}, {risk.data.assetsAnalysed} assets. The past does not
            predict the future — this is not investment advice.
          </p>
        </>
      )}
    </section>
  )
}

export function RiskFigure({
  label,
  value,
  tone,
  hint,
}: {
  label: string
  value: number | null | undefined
  tone?: 'down'
  hint: string
}) {
  return (
    <div className="risk-figure" title={hint}>
      <span className="label">{label}</span>
      <span className={`value ${value != null && tone ? tone : ''}`}>
        {value == null ? <span className="gap-inline">not available</span> : pct(value)}
      </span>
    </div>
  )
}
